import { useEffect, useRef, useState } from "react";
import "./CountDownTimer.css";

const FULL_DASH_ARRAY = 283;
const WARNING_THRESHOLD = 10;
const ALERT_THRESHOLD = 5;

const COLOR_CODES = {
  info: { color: "green" },
  warning: { color: "orange", threshold: WARNING_THRESHOLD },
  alert: { color: "red", threshold: ALERT_THRESHOLD },
};

export default function CountDownTimer({
  timeLimit = 25,
  onTimesUp,
  onStart,
  isFrozen = false,
  extendBy = 0,
  extendAmount = 25,
  extendMaxLimit = 50,
  displayValue = null,
  displayLimit = null,
  warningThreshold = WARNING_THRESHOLD,
  alertThreshold = ALERT_THRESHOLD,
}) {
  const manual =
    displayValue != null &&
    displayLimit != null &&
    Number.isFinite(Number(displayValue)) &&
    Number.isFinite(Number(displayLimit));
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const [activeLimit, setActiveLimit] = useState(timeLimit);
  const [remainingPathColor, setRemainingPathColor] = useState(COLOR_CODES.info.color);
  const lastExtendRef = useRef(0);
  const onTimesUpRef = useRef(onTimesUp);
  const shownLeft = manual ? Math.max(0, Math.round(Number(displayValue))) : timeLeft;
  const shownLimit = manual ? Math.max(1, Math.round(Number(displayLimit))) : activeLimit;

  onTimesUpRef.current = onTimesUp;

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    // Moins d'une minute : on affiche juste le nombre de secondes (ex. "12").
    if (minutes === 0) return `${seconds}`;
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  const calculateTimeFraction = (time, limit) => {
    const safeLimit = Math.max(1, limit);
    const rawTimeFraction = time / safeLimit;
    return rawTimeFraction - (1 / safeLimit) * (1 - rawTimeFraction);
  };

  const setCircleDasharray = (time, limit) => {
    const circleDasharray = `${(calculateTimeFraction(time, limit) * FULL_DASH_ARRAY).toFixed(0)} ${FULL_DASH_ARRAY}`;
    return circleDasharray;
  };

  useEffect(() => {
    if (extendBy <= 0 || extendBy === lastExtendRef.current) return;
    lastExtendRef.current = extendBy;
    setActiveLimit(extendMaxLimit);
    setTimeLeft((prev) => Math.min(prev + extendAmount, extendMaxLimit));
  }, [extendBy, extendAmount, extendMaxLimit]);

  useEffect(() => {
    if (shownLeft <= alertThreshold) {
      setRemainingPathColor(COLOR_CODES.alert.color);
    } else if (shownLeft <= warningThreshold) {
      setRemainingPathColor(COLOR_CODES.warning.color);
    } else {
      setRemainingPathColor(COLOR_CODES.info.color);
    }
  }, [shownLeft, alertThreshold, warningThreshold]);

  useEffect(() => {
    onStart?.();
  }, [onStart]);

  useEffect(() => {
    if (manual) return undefined;
    if (timeLeft === 0) {
      onTimesUpRef.current?.();
      return;
    }

    if (isFrozen) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft, isFrozen, manual]);

  return (
    <div className="base-timer">
      <svg
        className="base-timer__svg"
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g className="base-timer__circle">
          <circle className="base-timer__path-elapsed" cx="50" cy="50" r="45"></circle>
          <path
            className={`base-timer__path-remaining ${remainingPathColor}`}
            strokeDasharray={setCircleDasharray(shownLeft, shownLimit)}
            d="
              M 50, 50
              m -45, 0
              a 45,45 0 1,0 90,0
              a 45,45 0 1,0 -90,0
            "
          ></path>
        </g>
      </svg>
      <span className="base-timer__label">{formatTime(shownLeft)}</span>
    </div>
  );
}
